import * as Yup from "yup";
import { DATE_RE, EIN_RE, PHONE_RE, SSN_RE, ZIP_RE, isoToMaskedDate } from "@/lib/masks";

/**
 * The full onboarding field set (individual + business + spouse + address). A
 * single Formik form spans the whole wizard so autosave and cross-tab finish
 * validation share one values object. Every field is a string/boolean at the UI
 * layer; masked fields store their formatted value (see `@/lib/masks`).
 */
export interface OnboardingValues {
  // shared identity
  salutation: string;
  // individual identity
  first_name: string;
  middle_initial: string;
  last_name: string;
  ssn: string;
  date_of_birth: string;
  driver_license: string;
  license_state: string;
  home_phone: string;
  mobile_phone: string;
  fax_number: string;
  work_phone: string;
  work_mobile: string;
  preferred_contact_method: string;
  passport_on_file: string;
  is_trustee: boolean;
  trustee_name: string;
  trustee_role: string;
  is_deceased: boolean;
  date_of_death: string;
  // business identity
  business_name: string;
  dba: string;
  ein: string;
  // business contact
  business_contact_first_name: string;
  business_contact_last_name: string;
  business_contact_title: string;
  email: string;
  // spouse
  marital_status: string; // UNMARRIED | MARRIED
  date_of_marriage: string;
  in_community_property_state: boolean;
  spouse_first_name: string;
  spouse_middle_initial: string;
  spouse_last_name: string;
  spouse_ssn: string;
  spouse_date_of_birth: string;
  spouse_driver_license: string;
  spouse_license_state: string;
  spouse_home_phone: string;
  spouse_mobile_phone: string;
  spouse_fax_number: string;
  spouse_work_phone: string;
  spouse_work_mobile: string;
  spouse_preferred_contact_method: string;
  spouse_passport_on_file: string;
  spouse_email: string;
  spouse_salutation: string;
  spouse_is_trustee: boolean;
  spouse_trustee_name: string;
  spouse_trustee_role: string;
  spouse_is_deceased: boolean;
  spouse_date_of_death: string;
  // home address
  home_address_type: string; // DOMESTIC | INTERNATIONAL
  home_street: string;
  home_line2: string;
  home_city: string;
  home_state: string;
  home_zip: string;
  home_county: string;
  home_timezone: string;
  home_international_address: string;
  home_country: string;
  // mailing address
  mailing_is_different: boolean;
  mailing_address_type: string;
  mailing_street: string;
  mailing_line2: string;
  mailing_city: string;
  mailing_state: string;
  mailing_zip: string;
  mailing_county: string;
  mailing_international_address: string;
  mailing_country: string;
}

/** Coerce a server profile value (unknown) into a string for a text field. */
function s(v: unknown): string {
  if (v == null) return "";
  return String(v);
}

function b(v: unknown): boolean {
  return v === true || v === "true" || v === 1 || v === "1";
}

/** ISO or already-masked → `MM/DD/YYYY` for a DateField. */
function d(v: unknown): string {
  const raw = s(v);
  if (!raw) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return isoToMaskedDate(raw);
  return raw;
}

/**
 * Build the wizard's initial values from the server `profile` blob. Unknown /
 * missing keys default to empty. Radios default to their first option so the
 * conditional groups render predictably.
 */
export function buildInitialValues(profile: Record<string, unknown>): OnboardingValues {
  const p = profile ?? {};
  return {
    salutation: s(p.salutation),
    first_name: s(p.first_name),
    middle_initial: s(p.middle_initial),
    last_name: s(p.last_name),
    ssn: s(p.ssn),
    date_of_birth: d(p.date_of_birth),
    driver_license: s(p.driver_license),
    license_state: s(p.license_state),
    home_phone: s(p.home_phone),
    mobile_phone: s(p.mobile_phone),
    fax_number: s(p.fax_number),
    work_phone: s(p.work_phone),
    work_mobile: s(p.work_mobile),
    preferred_contact_method: s(p.preferred_contact_method),
    passport_on_file: s(p.passport_on_file),
    is_trustee: b(p.is_trustee),
    trustee_name: s(p.trustee_name),
    trustee_role: s(p.trustee_role),
    is_deceased: b(p.is_deceased),
    date_of_death: d(p.date_of_death),
    business_name: s(p.business_name),
    dba: s(p.dba),
    ein: s(p.ein),
    business_contact_first_name: s(p.business_contact_first_name),
    business_contact_last_name: s(p.business_contact_last_name),
    business_contact_title: s(p.business_contact_title),
    email: s(p.email),
    marital_status: s(p.marital_status) || "UNMARRIED",
    date_of_marriage: d(p.date_of_marriage),
    in_community_property_state: b(p.in_community_property_state),
    spouse_first_name: s(p.spouse_first_name),
    spouse_middle_initial: s(p.spouse_middle_initial),
    spouse_last_name: s(p.spouse_last_name),
    spouse_ssn: s(p.spouse_ssn),
    spouse_date_of_birth: d(p.spouse_date_of_birth),
    spouse_driver_license: s(p.spouse_driver_license),
    spouse_license_state: s(p.spouse_license_state),
    spouse_home_phone: s(p.spouse_home_phone),
    spouse_mobile_phone: s(p.spouse_mobile_phone),
    spouse_fax_number: s(p.spouse_fax_number),
    spouse_work_phone: s(p.spouse_work_phone),
    spouse_work_mobile: s(p.spouse_work_mobile),
    spouse_preferred_contact_method: s(p.spouse_preferred_contact_method),
    spouse_passport_on_file: s(p.spouse_passport_on_file),
    spouse_email: s(p.spouse_email),
    spouse_salutation: s(p.spouse_salutation),
    spouse_is_trustee: b(p.spouse_is_trustee),
    spouse_trustee_name: s(p.spouse_trustee_name),
    spouse_trustee_role: s(p.spouse_trustee_role),
    spouse_is_deceased: b(p.spouse_is_deceased),
    spouse_date_of_death: d(p.spouse_date_of_death),
    home_address_type: s(p.home_address_type) || "DOMESTIC",
    home_street: s(p.home_street),
    home_line2: s(p.home_line2),
    home_city: s(p.home_city),
    home_state: s(p.home_state),
    home_zip: s(p.home_zip),
    home_county: s(p.home_county),
    home_timezone: s(p.home_timezone),
    home_international_address: s(p.home_international_address),
    home_country: s(p.home_country),
    mailing_is_different: b(p.mailing_is_different),
    mailing_address_type: s(p.mailing_address_type) || "DOMESTIC",
    mailing_street: s(p.mailing_street),
    mailing_line2: s(p.mailing_line2),
    mailing_city: s(p.mailing_city),
    mailing_state: s(p.mailing_state),
    mailing_zip: s(p.mailing_zip),
    mailing_county: s(p.mailing_county),
    mailing_international_address: s(p.mailing_international_address),
    mailing_country: s(p.mailing_country),
  };
}

// --- validation --------------------------------------------------------------
// Client validation is UX only; the server re-validates on submit (§9). A phone
// is valid when blank or a complete 10-digit number.
const phoneOptional = Yup.string().test(
  "phone",
  "Enter a complete 10-digit phone number",
  (v) => !v || PHONE_RE.test(v),
);
const dateOptional = Yup.string().test(
  "date",
  "Use MM/DD/YYYY",
  (v) => !v || DATE_RE.test(v),
);

/**
 * Full cross-tab schema used at Finish. Conditional required fields switch on
 * `marital_status` and `home_address_type`. Individual vs business required
 * identity fields are toggled via `clientType` in `makeValidationSchema`.
 */
export function makeValidationSchema(clientType: "INDIVIDUAL" | "BUSINESS") {
  const isIndividual = clientType === "INDIVIDUAL";
  return Yup.object({
    // individual identity
    first_name: isIndividual
      ? Yup.string().required("First name is required")
      : Yup.string(),
    last_name: isIndividual
      ? Yup.string().required("Last name is required")
      : Yup.string(),
    middle_initial: Yup.string().max(1, "Max 1 character"),
    ssn: isIndividual
      ? Yup.string()
          .required("SSN is required")
          .matches(SSN_RE, "Enter a valid 9-digit SSN")
      : Yup.string(),
    date_of_birth: dateOptional,
    date_of_death: dateOptional,
    home_phone: phoneOptional,
    mobile_phone: phoneOptional,
    fax_number: phoneOptional,
    work_phone: phoneOptional,
    work_mobile: phoneOptional,
    // business identity
    business_name: isIndividual
      ? Yup.string()
      : Yup.string().required("Business name is required"),
    ein: isIndividual
      ? Yup.string()
      : Yup.string()
          .required("EIN is required")
          .matches(EIN_RE, "Enter a valid 9-digit EIN"),
    email: Yup.string().email("Enter a valid email"),
    spouse_email: Yup.string().when("marital_status", {
      is: "MARRIED",
      then: (sch) => sch.required("Spouse email is required").email("Enter a valid email"),
      otherwise: (sch) => sch.email("Enter a valid email"),
    }),
    // spouse required-when-married
    date_of_marriage: Yup.string().when("marital_status", {
      is: "MARRIED",
      then: (sch) =>
        sch.required("Marriage date is required").matches(DATE_RE, "Use MM/DD/YYYY"),
      otherwise: () => dateOptional,
    }),
    spouse_first_name: Yup.string().when("marital_status", {
      is: "MARRIED",
      then: (sch) => sch.required("Spouse first name is required"),
    }),
    spouse_last_name: Yup.string().when("marital_status", {
      is: "MARRIED",
      then: (sch) => sch.required("Spouse last name is required"),
    }),
    spouse_ssn: Yup.string().when("marital_status", {
      is: "MARRIED",
      then: (sch) =>
        sch.required("Spouse SSN is required").matches(SSN_RE, "Enter a valid 9-digit SSN"),
      otherwise: (sch) => sch.test("ssn", "Enter a valid 9-digit SSN", (v) => !v || SSN_RE.test(v)),
    }),
    spouse_date_of_birth: Yup.string().when("marital_status", {
      is: "MARRIED",
      then: (sch) =>
        sch.required("Spouse date of birth is required").matches(DATE_RE, "Use MM/DD/YYYY"),
      otherwise: () => dateOptional,
    }),
    spouse_home_phone: phoneOptional,
    spouse_mobile_phone: phoneOptional,
    spouse_fax_number: phoneOptional,
    spouse_work_phone: phoneOptional,
    spouse_work_mobile: phoneOptional,
    spouse_date_of_death: dateOptional,
    // home address — domestic vs international
    home_street: Yup.string().when("home_address_type", {
      is: "DOMESTIC",
      then: (sch) => sch.required("Street address is required"),
    }),
    home_city: Yup.string().when("home_address_type", {
      is: "DOMESTIC",
      then: (sch) => sch.required("City is required"),
    }),
    home_state: Yup.string().when("home_address_type", {
      is: "DOMESTIC",
      then: (sch) => sch.required("State is required"),
    }),
    home_zip: Yup.string().when("home_address_type", {
      is: "DOMESTIC",
      then: (sch) =>
        sch.required("ZIP is required").matches(ZIP_RE, "Enter a valid ZIP code"),
      otherwise: (sch) => sch.test("zip", "Enter a valid ZIP code", (v) => !v || ZIP_RE.test(v)),
    }),
    home_country: Yup.string().when("home_address_type", {
      is: "INTERNATIONAL",
      then: (sch) => sch.required("Country is required"),
    }),
    // mailing (only when different)
    mailing_street: Yup.string().when(["mailing_is_different", "mailing_address_type"], {
      is: (diff: boolean, type: string) => diff && type === "DOMESTIC",
      then: (sch) => sch.required("Mailing street is required"),
    }),
    mailing_city: Yup.string().when(["mailing_is_different", "mailing_address_type"], {
      is: (diff: boolean, type: string) => diff && type === "DOMESTIC",
      then: (sch) => sch.required("Mailing city is required"),
    }),
    mailing_state: Yup.string().when(["mailing_is_different", "mailing_address_type"], {
      is: (diff: boolean, type: string) => diff && type === "DOMESTIC",
      then: (sch) => sch.required("Mailing state is required"),
    }),
    mailing_zip: Yup.string().when(["mailing_is_different", "mailing_address_type"], {
      is: (diff: boolean, type: string) => diff && type === "DOMESTIC",
      then: (sch) =>
        sch.required("Mailing ZIP is required").matches(ZIP_RE, "Enter a valid ZIP code"),
      otherwise: (sch) => sch.test("zip", "Enter a valid ZIP code", (v) => !v || ZIP_RE.test(v)),
    }),
    mailing_country: Yup.string().when(["mailing_is_different", "mailing_address_type"], {
      is: (diff: boolean, type: string) => diff && type === "INTERNATIONAL",
      then: (sch) => sch.required("Mailing country is required"),
    }),
  });
}

/**
 * Which tab index each field lives on, per client type, so a server/finish
 * error can mark the right tab red and jump to it. Index space:
 *  Individual: 0 Intro · 1 Personal · 2 Spouse · 3 Address · 4 Finish
 *  Business:   0 Intro · 1 Business · 2 Contact · 3 Address · 4 Finish
 */
export function fieldTabIndex(clientType: "INDIVIDUAL" | "BUSINESS"): Record<string, number> {
  const address = 3;
  if (clientType === "INDIVIDUAL") {
    const personal = 1;
    const spouse = 2;
    return {
      first_name: personal,
      middle_initial: personal,
      last_name: personal,
      ssn: personal,
      date_of_birth: personal,
      driver_license: personal,
      license_state: personal,
      home_phone: personal,
      mobile_phone: personal,
      fax_number: personal,
      work_phone: personal,
      work_mobile: personal,
      preferred_contact_method: personal,
      passport_on_file: personal,
      trustee_name: personal,
      trustee_role: personal,
      date_of_death: personal,
      salutation: personal,
      marital_status: spouse,
      date_of_marriage: spouse,
      in_community_property_state: spouse,
      spouse_first_name: spouse,
      spouse_middle_initial: spouse,
      spouse_last_name: spouse,
      spouse_ssn: spouse,
      spouse_date_of_birth: spouse,
      spouse_email: spouse,
      spouse_home_phone: spouse,
      spouse_mobile_phone: spouse,
      spouse_fax_number: spouse,
      spouse_work_phone: spouse,
      spouse_work_mobile: spouse,
      ...addressFields(address),
    };
  }
  const business = 1;
  const contact = 2;
  return {
    business_name: business,
    dba: business,
    ein: business,
    work_phone: business,
    mobile_phone: business,
    fax_number: business,
    business_contact_first_name: contact,
    business_contact_last_name: contact,
    business_contact_title: contact,
    email: contact,
    salutation: contact,
    home_phone: contact,
    preferred_contact_method: contact,
    ...addressFields(address),
  };
}

function addressFields(idx: number): Record<string, number> {
  return {
    home_address_type: idx,
    home_street: idx,
    home_line2: idx,
    home_city: idx,
    home_state: idx,
    home_zip: idx,
    home_county: idx,
    home_timezone: idx,
    home_international_address: idx,
    home_country: idx,
    mailing_is_different: idx,
    mailing_address_type: idx,
    mailing_street: idx,
    mailing_line2: idx,
    mailing_city: idx,
    mailing_state: idx,
    mailing_zip: idx,
    mailing_county: idx,
    mailing_international_address: idx,
    mailing_country: idx,
  };
}
